import { Sparkles, ShieldCheck } from "lucide-react";

/**
 * Small green trust-tier pill shown on Advanced Search result cards.
 *
 * Renders in two states:
 *  - `Flagship` (Sparkles icon)     — the row's `source_tier === "editorial"`.
 *    These are the hand-curated seeds (NL WO-14, FR Top-12, DE Top-12,
 *    UK curates, US flagships) — every field verified against the
 *    university's own study-programme page.
 *  - `Verified` (ShieldCheck icon)  — the row was admin-audited
 *    (`content_verified === true`) via the /admin/verify workflow or
 *    university claim flow. Not editorial-tier but still hand-checked.
 *
 * Nothing renders when neither signal is set. Families spotting either
 * badge know they can trust the tuition / offer-rate / entry-req
 * numbers without cross-checking the university's own page.
 */
export default function TrustTierBadge({ row, className = "" }) {
  const isEditorial = row?.source_tier === "editorial";
  const isVerified = row?.content_verified === true;
  if (!isEditorial && !isVerified) return null;

  // Editorial (flagship) wins the visual — it's the stronger signal.
  const flagship = isEditorial;
  const Icon = flagship ? Sparkles : ShieldCheck;
  const label = flagship ? "Flagship" : "Verified";
  const testid = flagship ? "trust-tier-badge-flagship" : "trust-tier-badge-verified";
  const title = flagship
    ? "Hand-curated editorial flagship — tuition, entry requirements, offer rate and English test all cross-checked against the university's own study-programme page."
    : "Admin-audited record — tuition, offer rate and entry requirements verified against the university's official admissions page.";

  return (
    <span
      data-testid={testid}
      className={
        "inline-flex items-center gap-1 text-[10px] font-underground-sc " +
        "tracking-wider rounded-full px-2 py-0.5 leading-tight border " +
        (flagship
          ? "bg-emerald-50 text-emerald-800 border-emerald-300"
          : "bg-teal-50 text-teal-800 border-teal-300") +
        (className ? ` ${className}` : "")
      }
      title={title}
    >
      <Icon className="h-3 w-3 shrink-0" />
      {label}
    </span>
  );
}
